import type {
  ArchiveOrgUnitResponse,
  CreateOrgInvitationRequest,
  CreateOrgInvitationResponse,
  CreateOrgUnitRequest,
  CreateOrgUnitResponse,
  ListOrgInvitationsResponse,
  ListOrgMembersResponse,
  ListOrgMemberModelPermissionsResponse,
  ListOrgUnitModelPermissionsResponse,
  ListOrgUnitsResponse,
  ListTenantAuditEventsResponse,
  ReplaceOrgMemberModelPermissionsRequest,
  ReplaceOrgMemberModelPermissionsResponse,
  ReplaceOrgUnitModelPermissionsRequest,
  ReplaceOrgUnitModelPermissionsResponse,
  RevokeOrgInvitationResponse,
  UpdateOrgMemberRequest,
  UpdateOrgMemberResponse,
  UpdateOrgUnitRequest,
  UpdateOrgUnitResponse,
} from '@nadoutong/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  archiveOrgUnit,
  createOrgInvitation,
  createOrgUnit,
  type ListTenantAuditEventsQuery,
  listOrgInvitations,
  listOrgMemberModelPermissions,
  listOrgMembers,
  listOrgUnitModelPermissions,
  listOrgUnits,
  listTenantAuditEvents,
  replaceOrgMemberModelPermissions,
  replaceOrgUnitModelPermissions,
  revokeOrgInvitation,
  updateOrgMember,
  updateOrgUnit,
} from './orgs.ts';

/**
 * React Query wrappers for the `/v1/orgs/*` surface (members + invitation
 * lifecycle). Each key includes the `orgId` so callers with multi-org
 * sessions in the future get independent caches.
 *
 * Invalidation map:
 *   - createOrgInvitation -> invalidate invitation list for the org
 *   - revokeOrgInvitation -> invalidate invitation list for the org
 */

export const ORG_MEMBERS_KEY = (orgId: string) => ['orgs', orgId, 'members'] as const;
export const ORG_UNITS_KEY = (orgId: string) => ['orgs', orgId, 'org-units'] as const;
export const ORG_INVITATIONS_KEY = (orgId: string) =>
  ['orgs', orgId, 'invitations'] as const;
export const ORG_AUDIT_EVENTS_KEY = (
  orgId: string,
  query: ListTenantAuditEventsQuery | undefined,
) =>
  [
    'orgs',
    orgId,
    'audit-events',
    query?.limit ?? '',
    query?.offset ?? '',
    query?.action ?? '',
    query?.targetAccountId ?? '',
  ] as const;
export const ORG_MEMBER_MODEL_PERMISSIONS_KEY = (orgId: string, accountId: string) =>
  ['orgs', orgId, 'members', accountId, 'model-permissions'] as const;
export const ORG_UNIT_MODEL_PERMISSIONS_KEY = (orgId: string, orgUnitId: string) =>
  ['orgs', orgId, 'org-units', orgUnitId, 'model-permissions'] as const;

export function useOrgMembers(
  orgId: string | undefined,
): UseQueryResult<ListOrgMembersResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: orgId === undefined ? ['orgs', 'missing', 'members'] : ORG_MEMBERS_KEY(orgId),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error('orgId is required'));
      }
      return listOrgMembers(orgId, fetchImpl, signal);
    },
    enabled: orgId !== undefined,
  });
}

export function useOrgUnits(
  orgId: string | undefined,
): UseQueryResult<ListOrgUnitsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: orgId === undefined ? ['orgs', 'missing', 'org-units'] : ORG_UNITS_KEY(orgId),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error('orgId is required'));
      }
      return listOrgUnits(orgId, fetchImpl, signal);
    },
    enabled: orgId !== undefined,
  });
}

export interface CreateOrgUnitVars {
  readonly orgId: string;
  readonly body: CreateOrgUnitRequest;
}

export function useCreateOrgUnit(): UseMutationResult<
  CreateOrgUnitResponse,
  Error,
  CreateOrgUnitVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => createOrgUnit(orgId, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ORG_UNITS_KEY(variables.orgId) });
      void qc.invalidateQueries({ queryKey: ORG_MEMBERS_KEY(variables.orgId) });
    },
  });
}

export interface UpdateOrgUnitVars {
  readonly orgId: string;
  readonly orgUnitId: string;
  readonly body: UpdateOrgUnitRequest;
}

export function useUpdateOrgUnit(): UseMutationResult<
  UpdateOrgUnitResponse,
  Error,
  UpdateOrgUnitVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, orgUnitId, body }) =>
      updateOrgUnit(orgId, orgUnitId, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ORG_UNITS_KEY(variables.orgId) });
      void qc.invalidateQueries({ queryKey: ORG_MEMBERS_KEY(variables.orgId) });
    },
  });
}

export interface ArchiveOrgUnitVars {
  readonly orgId: string;
  readonly orgUnitId: string;
}

export function useArchiveOrgUnit(): UseMutationResult<
  ArchiveOrgUnitResponse,
  Error,
  ArchiveOrgUnitVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, orgUnitId }) => archiveOrgUnit(orgId, orgUnitId, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ORG_UNITS_KEY(variables.orgId) });
      void qc.invalidateQueries({ queryKey: ORG_MEMBERS_KEY(variables.orgId) });
    },
  });
}

export interface UpdateOrgMemberVars {
  readonly orgId: string;
  readonly accountId: string;
  readonly body: UpdateOrgMemberRequest;
}

export function useUpdateOrgMember(): UseMutationResult<
  UpdateOrgMemberResponse,
  Error,
  UpdateOrgMemberVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, accountId, body }) =>
      updateOrgMember(orgId, accountId, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ORG_MEMBERS_KEY(variables.orgId) });
      void qc.invalidateQueries({ queryKey: ORG_UNITS_KEY(variables.orgId) });
    },
  });
}

export function useOrgInvitations(
  orgId: string | undefined,
): UseQueryResult<ListOrgInvitationsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey:
      orgId === undefined ? ['orgs', 'missing', 'invitations'] : ORG_INVITATIONS_KEY(orgId),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error('orgId is required'));
      }
      return listOrgInvitations(orgId, fetchImpl, signal);
    },
    enabled: orgId !== undefined,
  });
}

export function useTenantAuditEvents(
  orgId: string | undefined,
  query?: ListTenantAuditEventsQuery,
  enabled = true,
): UseQueryResult<ListTenantAuditEventsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey:
      orgId === undefined
        ? ['orgs', 'missing', 'audit-events']
        : ORG_AUDIT_EVENTS_KEY(orgId, query),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error('orgId is required'));
      }
      return listTenantAuditEvents(orgId, fetchImpl, query, signal);
    },
    enabled: enabled && orgId !== undefined,
  });
}

export function useOrgMemberModelPermissions(
  orgId: string | undefined,
  accountId: string | undefined,
  enabled = true,
): UseQueryResult<ListOrgMemberModelPermissionsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey:
      orgId === undefined || accountId === undefined
        ? ['orgs', 'missing', 'members', 'missing', 'model-permissions']
        : ORG_MEMBER_MODEL_PERMISSIONS_KEY(orgId, accountId),
    queryFn: ({ signal }) => {
      if (orgId === undefined || accountId === undefined) {
        return Promise.reject(new Error('orgId and accountId are required'));
      }
      return listOrgMemberModelPermissions(orgId, accountId, fetchImpl, signal);
    },
    enabled: enabled && orgId !== undefined && accountId !== undefined,
  });
}

export function useOrgUnitModelPermissions(
  orgId: string | undefined,
  orgUnitId: string | undefined,
  enabled = true,
): UseQueryResult<ListOrgUnitModelPermissionsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey:
      orgId === undefined || orgUnitId === undefined
        ? ['orgs', 'missing', 'org-units', 'missing', 'model-permissions']
        : ORG_UNIT_MODEL_PERMISSIONS_KEY(orgId, orgUnitId),
    queryFn: ({ signal }) => {
      if (orgId === undefined || orgUnitId === undefined) {
        return Promise.reject(new Error('orgId and orgUnitId are required'));
      }
      return listOrgUnitModelPermissions(orgId, orgUnitId, fetchImpl, signal);
    },
    enabled: enabled && orgId !== undefined && orgUnitId !== undefined,
  });
}

export interface CreateInvitationVars {
  readonly orgId: string;
  readonly body: CreateOrgInvitationRequest;
}

export function useCreateOrgInvitation(): UseMutationResult<
  CreateOrgInvitationResponse,
  Error,
  CreateInvitationVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => createOrgInvitation(orgId, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ORG_INVITATIONS_KEY(variables.orgId) });
    },
  });
}

export interface RevokeInvitationVars {
  readonly orgId: string;
  readonly invitationId: string;
}

export function useRevokeOrgInvitation(): UseMutationResult<
  RevokeOrgInvitationResponse,
  Error,
  RevokeInvitationVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, invitationId }) =>
      revokeOrgInvitation(orgId, invitationId, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ORG_INVITATIONS_KEY(variables.orgId) });
    },
  });
}

export interface ReplaceMemberModelPermissionsVars {
  readonly orgId: string;
  readonly accountId: string;
  readonly body: ReplaceOrgMemberModelPermissionsRequest;
}

export function useReplaceOrgMemberModelPermissions(): UseMutationResult<
  ReplaceOrgMemberModelPermissionsResponse,
  Error,
  ReplaceMemberModelPermissionsVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, accountId, body }) =>
      replaceOrgMemberModelPermissions(orgId, accountId, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ORG_MEMBER_MODEL_PERMISSIONS_KEY(variables.orgId, variables.accountId),
      });
      void qc.invalidateQueries({ queryKey: ORG_MEMBERS_KEY(variables.orgId) });
    },
  });
}

export interface ReplaceOrgUnitModelPermissionsVars {
  readonly orgId: string;
  readonly orgUnitId: string;
  readonly body: ReplaceOrgUnitModelPermissionsRequest;
}

export function useReplaceOrgUnitModelPermissions(): UseMutationResult<
  ReplaceOrgUnitModelPermissionsResponse,
  Error,
  ReplaceOrgUnitModelPermissionsVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, orgUnitId, body }) =>
      replaceOrgUnitModelPermissions(orgId, orgUnitId, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: ORG_UNIT_MODEL_PERMISSIONS_KEY(variables.orgId, variables.orgUnitId),
      });
      void qc.invalidateQueries({ queryKey: ORG_UNITS_KEY(variables.orgId) });
      void qc.invalidateQueries({ queryKey: ORG_MEMBERS_KEY(variables.orgId) });
    },
  });
}
